import { uid as getUid } from "@dhx/ts-common/core";
import { sv } from "@dhx/ts-common/dom";
import { linearGradient, verticalCenteredText } from "../helpers/common";
import { IScale, ISeriaConfig, PointData, PointType, ScaleType, SvgElement, TooltipType } from "../types";
import ScaleSeria from "./ScaleSeria";

export default class Bar extends ScaleSeria {
	protected _shift = 0;

	protected _baseLinePosition: number;

	addScale(type: ScaleType, scale: IScale): void {
		super.addScale(type, scale);
		scale.addPadding();
	}
	seriesShift(shift:number):number{
		this._shift = shift;
		return this.config.barWidth;
	}
	paint(width: number, height: number, prev?: PointData[]): SvgElement {
		super.paint(width, height);

		if (!this.config.active) {
			return null;
		}
		let svg = [];
		if (this._gradient) {
			svg.push(sv("defs", [this._gradient()]));
		}

		const css = `chart ${this.config.type} ${this.config.css || ""} ${this.config.dashed ? "dash-line" : ""}`;
		const form = this._getForm(this._points, css, width, height, prev);
		svg = svg.concat(form);
		return sv("g", {class: "seria", _key: this.id}, svg);
	}
	getTooltipType(_id: string, _x?: number, y?: number): TooltipType {
		if (this.config.baseLine !== undefined && this._baseLinePosition < y) {
			return TooltipType.bot;
		}
		return TooltipType.top;
	}
	protected _getClosestDist(x, y, px, py) {
		if (this.config.stacked && y < py) {
			return Infinity;
		}
		return Math.abs(x - px);
	}
	protected _path(item, prev):string{
		item[0] += this._shift;
		return `
M ${item[0] - (this.config.barWidth / 2)} ${prev}
V ${item[1]}
h ${this.config.barWidth}
V ${prev}`;
	}

	protected _base(height){
		const baseLine = this.config.baseLine;
		return this._baseLinePosition = baseLine !== undefined ? this.yScale.point(baseLine)*height : height - 1;
	}

	protected _text(item, prev){
		return {
			x: item[0],
			y: (prev + item[1]) / 2,
			class: "bar-text"
		};
	}

	protected _getForm(points: PointData[], css:string, _width: number, height: number, prev: PointData[]): object {
		const { fill, alpha, showText, showTextTemplate } = this.config;

		const svg = [];
		const base = this._base(height);
		const getPrev = index => !prev ? base : prev[index][1];

		const series = points.map((item, index) => sv("path", {
			"_key": "seria" + this.config.id + index,
			"d": this._path(item, getPrev(index)),
			"class": css,
			fill,
			"fill-opacity": alpha
		}));
		svg.push(...series);

		if (showText || showTextTemplate) {
			const isWrite = (item, index) => Math.abs(getPrev(index) - item[1]) > 16 ? true : false; // hide text, where height < 16
			const text = points.map((item, index) => {
				const value = this._getText(item);
				return isWrite(item, index) ? sv("text", this._text(item, getPrev(index)),
					[showTextTemplate ? verticalCenteredText(showTextTemplate(value)) : verticalCenteredText(value)]) : null;
			});
			svg.push(...text);
		}
		return svg;
	}
	protected _getText(item) {
		return item[4].toString();
	}
	protected _setDefaults(config: ISeriaConfig) {
		const defaults = {
			barWidth: 30,
			alpha: 1,
			active: true,
			tooltip: true,
			pointType: PointType.empty
		};
		this.config = {...defaults, ...config};
		const showTooltip = this.config.tooltip;
		const point = this.config.pointType;
		const color = this.config.pointColor || this.config.color;

		if (point) {
			this.config.pointType = point;
			this._drawPointType = this._getPointType(point, color, showTooltip);
		}
		if (this.config.gradient) {
			const id = "gradient" + getUid();
			const gradient = this.config.gradient(this.config.fill);
			this._gradient = () => linearGradient(gradient, id);
			this.config.fill = `url(#${id})`;
		}
	}
}